import React, { useState } from 'react';
import {
  validateActivityPayload,
  MUSICAL_TOKENS,
  normalizeTokenArray,
  VexRhythmGlyph,
  tokenToLabel,
  type ExamResourceType,
  type GameMode,
  type EvaluationBlock,
} from '@interactive-room';
import { BuilderInput, BuilderTextarea } from './BuilderField';
import { SequencerStepsCollapsible } from './SequencerStepsCollapsible';
import { MusicCanvasEditor, isMusicNativeMode } from './MusicCanvasEditor';
import { PrintMatrixPreview } from '../print/PrintMatrixPreview';
import { RhythmPatternStrip } from '../print/RhythmPatternStrip';
import { BlockAiGenerateDialog } from './BlockAiGenerateDialog';
import { supportsBlockAi } from '../constants/blockAiParams';
import type { ExamLevelId } from '../constants/examAiParams';
import {
  GuideEditorialBlockEditor,
  isGuideEditorialMode,
} from './guide/GuideEditorialBlockEditors';

export interface ExamBlockExamContext {
  examTitle: string;
  subjectTag: string;
  resourceType: ExamResourceType;
  blockIndex: number;
  guideContext?: string;
  subjectExpertPrompt?: string;
  level?: ExamLevelId;
}

interface ExamBlockInlineEditorProps {
  block: EvaluationBlock;
  onChange: (block: EvaluationBlock) => void;
  examContext?: ExamBlockExamContext;
  /** Cuando false, oculta el chrome «Regenerar con IA» (p. ej. si el parent ya lo ofrece). */
  showAiControls?: boolean;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3">
      <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--sala-field-dim)]">{title}</p>
      {children}
    </div>
  );
}

function StimulusFields({
  payload,
  setPayload,
}: {
  payload: Record<string, unknown>;
  setPayload: (payload: Record<string, unknown>) => void;
}) {
  const stimulus = (payload.stimulus ?? {}) as Record<string, unknown>;
  const updateStimulus = (patch: Record<string, unknown>) => {
    const next = { ...stimulus, ...patch };
    if (!String(next.content || '').trim()) {
      const { stimulus: _removed, ...rest } = payload;
      setPayload(rest);
      return;
    }
    setPayload({ ...payload, stimulus: next });
  };

  return (
    <Section title="Texto ancla · stimulus">
      <BuilderInput
        label="Título del texto (poema, fuente, caso…)"
        value={String(stimulus.title ?? '')}
        onChange={(e) => updateStimulus({ title: e.target.value })}
      />
      <label className="block">
        <span className="sala-label">Tipo</span>
        <select
          className="sala-input-dark mt-2 w-full text-sm"
          value={String(stimulus.type ?? 'prose')}
          onChange={(e) => updateStimulus({ type: e.target.value })}
        >
          <option value="poem">Poema / verso</option>
          <option value="prose">Prosa</option>
          <option value="historical_source">Fuente histórica</option>
          <option value="case_study">Caso de estudio</option>
          <option value="scientific_data">Datos científicos</option>
          <option value="general">General</option>
        </select>
      </label>
      <BuilderTextarea
        label="Contenido íntegro"
        hint="Poema, carta, párrafo o datos que el alumno lee antes de responder."
        value={String(stimulus.content ?? '')}
        onChange={(e) => updateStimulus({ type: stimulus.type ?? 'prose', content: e.target.value })}
      />
    </Section>
  );
}

function AdvancedPayloadEditor({
  payload,
  onPayloadChange,
}: {
  payload: Record<string, unknown>;
  onPayloadChange: (payload: Record<string, unknown>) => void;
}) {
  const [open, setOpen] = useState(false);
  const title = String(payload.title ?? '');
  const bodyText = String(payload.bodyText ?? payload.body ?? payload.prompt ?? '');

  return (
    <div className="guide-block-editor space-y-4">
      <p className="text-sm leading-relaxed text-[var(--sala-ink-muted)]">
        Este tipo de bloque aún no tiene un formulario pedagógico completo. Puedes editar el título y el
        texto principal abajo; el resto de campos técnicos queda oculto en «modo avanzado».
      </p>
      <BuilderInput
        label="Título"
        value={title}
        onChange={(e) => onPayloadChange({ ...payload, title: e.target.value })}
      />
      <BuilderTextarea
        label="Texto / consigna"
        value={bodyText}
        onChange={(e) => {
          const next = e.target.value;
          if ('bodyText' in payload) onPayloadChange({ ...payload, bodyText: next });
          else if ('body' in payload) onPayloadChange({ ...payload, body: next });
          else if ('prompt' in payload) onPayloadChange({ ...payload, prompt: next });
          else onPayloadChange({ ...payload, bodyText: next });
        }}
        className="min-h-[100px]"
      />
      <button
        type="button"
        className="text-[11px] font-semibold text-[var(--sala-accent-neon)] hover:underline"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? 'Ocultar modo avanzado' : 'Mostrar modo avanzado (técnico)'}
      </button>
      {open ? (
        <BuilderTextarea
          label="Datos del bloque (avanzado)"
          hint="Solo si sabes lo que estás haciendo — un error de sintaxis puede romper el bloque."
          value={JSON.stringify(payload, null, 2)}
          onChange={(e) => {
            try {
              onPayloadChange(JSON.parse(e.target.value) as Record<string, unknown>);
            } catch {
              /* ignore invalid json while typing */
            }
          }}
          className="min-h-[160px] font-mono text-[11px]"
        />
      ) : null}
    </div>
  );
}

function ArenaEditor({
  payload,
  onPayloadChange,
}: {
  payload: Record<string, unknown>;
  onPayloadChange: (payload: Record<string, unknown>) => void;
}) {
  const questions = Array.isArray(payload.questions) ? payload.questions : [];
  const [qIndex, setQIndex] = useState(0);
  const question = questions[qIndex] as Record<string, unknown> | undefined;

  const updateQuestion = (patch: Record<string, unknown>) => {
    const next = questions.map((q, i) => (i === qIndex ? { ...(q as object), ...patch } : q));
    onPayloadChange({ ...payload, questions: next });
  };

  if (!question) return null;
  const options = Array.isArray(question.options) ? (question.options as string[]) : [];

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {questions.map((_, i) => (
          <button
            key={i}
            type="button"
            className={`rounded-md px-2 py-1 text-[10px] font-bold ${i === qIndex ? 'bg-[var(--sala-accent)] text-white' : 'bg-white/70'}`}
            onClick={() => setQIndex(i)}
          >
            P {i + 1}
          </button>
        ))}
        <button
          type="button"
          className="rounded-md bg-white/70 px-2 py-1 text-[10px] font-bold"
          onClick={() => {
            onPayloadChange({
              ...payload,
              questions: [
                ...questions,
                {
                  id: `q${questions.length + 1}`,
                  kind: 'multiple-choice',
                  prompt: '',
                  options: ['', '', '', ''],
                  correctIndex: 0,
                },
              ],
            });
            setQIndex(questions.length);
          }}
        >
          + Pregunta
        </button>
      </div>
      <BuilderTextarea
        label="Consigna"
        value={String(question.prompt ?? '')}
        onChange={(e) => updateQuestion({ prompt: e.target.value })}
      />
      <Section title="Opciones · doble clic marca la correcta">
        <div className="grid gap-2 sm:grid-cols-2">
          {options.map((opt, i) => {
            const correct = Number(question.correctIndex ?? 0) === i;
            return (
              <button
                key={i}
                type="button"
                className={`rounded-lg border px-3 py-2 text-left ${correct ? 'border-emerald-500 bg-emerald-50' : 'border-[var(--sala-field-border)] bg-white/80'}`}
                onDoubleClick={() => updateQuestion({ correctIndex: i })}
              >
                <input
                  className="w-full bg-transparent text-sm font-semibold"
                  value={opt}
                  placeholder={`Opción ${String.fromCharCode(65 + i)}`}
                  onChange={(e) => {
                    const next = [...options];
                    next[i] = e.target.value;
                    updateQuestion({ options: next });
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </button>
            );
          })}
        </div>
      </Section>
    </>
  );
}

function OrderBlockEditor({
  block,
  payload,
  items,
  setTitle,
  setPayload,
}: {
  block: EvaluationBlock;
  payload: Record<string, unknown>;
  items: string[];
  setTitle: (title: string) => void;
  setPayload: (payload: Record<string, unknown>) => void;
}) {
  const [expandedStep, setExpandedStep] = useState(0);
  return (
    <div className="space-y-4">
      <BuilderInput label="Título" value={String(payload.title ?? block.label ?? '')} onChange={(e) => setTitle(e.target.value)} />
      <BuilderInput label="Instrucción" value={String(payload.prompt ?? '')} onChange={(e) => setPayload({ ...payload, prompt: e.target.value })} />
      <SequencerStepsCollapsible
        items={items}
        expandedIndex={expandedStep}
        onExpandedChange={setExpandedStep}
        onItemChange={(index, text) => {
          const next = [...items];
          next[index] = text;
          setPayload({ ...payload, items: next, correctOrder: next.map((_, i) => i) });
        }}
        onAdd={() => {
          const next = [...items, `Paso ${items.length + 1}`];
          setPayload({ ...payload, items: next, correctOrder: next.map((_, i) => i) });
          setExpandedStep(items.length);
        }}
        onRemove={(index) => {
          const next = items.filter((_, i) => i !== index);
          setPayload({ ...payload, items: next, correctOrder: next.map((_, i) => i) });
          setExpandedStep((prev) => Math.max(0, prev >= index ? prev - 1 : prev));
        }}
      />
    </div>
  );
}

export function ExamBlockInlineEditor({
  block,
  onChange,
  examContext,
  showAiControls = true,
}: ExamBlockInlineEditorProps) {
  const [aiOpen, setAiOpen] = useState(false);
  const payload = (block.payload ?? {}) as Record<string, unknown>;
  const canAi =
    showAiControls &&
    examContext &&
    supportsBlockAi(block.mode, examContext.resourceType, examContext.subjectTag);

  const setPayload = (next: Record<string, unknown>) => {
    onChange({ ...block, payload: next as EvaluationBlock['payload'] });
  };

  const setTitle = (title: string) => {
    onChange({ ...block, label: title, payload: { ...payload, title } as EvaluationBlock['payload'] });
  };

  let editorBody: React.ReactNode;

  switch (block.mode as GameMode) {
    case 'arena':
      editorBody = (
        <div className="space-y-4">
          <BuilderInput label="Título del bloque" value={String(payload.title ?? block.label ?? '')} onChange={(e) => setTitle(e.target.value)} />
          <ArenaEditor payload={payload} onPayloadChange={setPayload} />
        </div>
      );
      break;

    case 'order': {
      const items = Array.isArray(payload.items) ? payload.items.map(String) : [];
      editorBody = (
        <OrderBlockEditor
          block={block}
          payload={payload}
          items={items}
          setTitle={setTitle}
          setPayload={setPayload}
        />
      );
      break;
    }

    case 'completar':
      editorBody = (
        <div className="space-y-4">
          <BuilderInput label="Título" value={String(payload.title ?? block.label ?? '')} onChange={(e) => setTitle(e.target.value)} />
          <StimulusFields payload={payload} setPayload={setPayload} />
          <BuilderTextarea
            label="Texto con huecos [palabra]"
            hint="Usa corchetes para los espacios a completar."
            value={String(payload.text ?? '')}
            onChange={(e) => setPayload({ ...payload, text: e.target.value })}
          />
          <BuilderInput
            label="Distractores (separados por coma)"
            value={Array.isArray(payload.distractors) ? payload.distractors.join(', ') : ''}
            onChange={(e) =>
              setPayload({
                ...payload,
                distractors: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
              })
            }
          />
        </div>
      );
      break;

    case 'vincula': {
      const pairs = Array.isArray(payload.pairs) ? payload.pairs : [];
      editorBody = (
        <div className="space-y-4">
          <BuilderInput label="Título" value={String(payload.title ?? block.label ?? '')} onChange={(e) => setTitle(e.target.value)} />
          {pairs.map((pair, index) => {
            const row = pair as Record<string, unknown>;
            return (
              <div key={String(row.id ?? index)} className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
                <input
                  className="sala-input-dark text-sm"
                  value={String(row.ladoA ?? '')}
                  placeholder="Columna A"
                  onChange={(e) => {
                    const next = pairs.map((p, i) => (i === index ? { ...(p as object), ladoA: e.target.value } : p));
                    setPayload({ ...payload, pairs: next });
                  }}
                />
                <input
                  className="sala-input-dark text-sm"
                  value={String(row.ladoB ?? '')}
                  placeholder="Columna B"
                  onChange={(e) => {
                    const next = pairs.map((p, i) => (i === index ? { ...(p as object), ladoB: e.target.value } : p));
                    setPayload({ ...payload, pairs: next });
                  }}
                />
                <button
                  type="button"
                  className="text-xs text-red-500"
                  onClick={() => setPayload({ ...payload, pairs: pairs.filter((_, i) => i !== index) })}
                >
                  ×
                </button>
              </div>
            );
          })}
          <button
            type="button"
            className="sala-btn-outline text-xs"
            onClick={() =>
              setPayload({
                ...payload,
                pairs: [...pairs, { id: `p${pairs.length + 1}`, ladoA: '', ladoB: '' }],
              })
            }
          >
            + Par
          </button>
        </div>
      );
      break;
    }

    case 'desarrollo': {
      const rubric = Array.isArray(payload.rubric) ? payload.rubric.map(String) : [];
      editorBody = (
        <div className="space-y-4">
          <BuilderInput label="Título" value={String(payload.title ?? block.label ?? '')} onChange={(e) => setTitle(e.target.value)} />
          <BuilderTextarea label="Consigna" value={String(payload.prompt ?? '')} onChange={(e) => setPayload({ ...payload, prompt: e.target.value })} />
          <StimulusFields payload={payload} setPayload={setPayload} />
          <BuilderTextarea
            label="Texto de lectura (sourceText)"
            hint="Legacy — preferir Texto ancla arriba. Se migra automáticamente al imprimir."
            value={String(payload.sourceText ?? '')}
            onChange={(e) => setPayload({ ...payload, sourceText: e.target.value })}
          />
          <BuilderTextarea
            label="Rúbrica (un criterio por línea)"
            value={rubric.join('\n')}
            onChange={(e) =>
              setPayload({
                ...payload,
                rubric: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean),
              })
            }
          />
        </div>
      );
      break;
    }

    case 'agrupa': {
      const categories = Array.isArray(payload.categories) ? payload.categories : [];
      const items = Array.isArray(payload.items) ? payload.items : [];
      editorBody = (
        <div className="space-y-4">
          <BuilderInput label="Título" value={String(payload.title ?? block.label ?? '')} onChange={(e) => setTitle(e.target.value)} />
          <Section title="Categorías">
            {categories.map((cat, index) => {
              const row = cat as Record<string, unknown>;
              return (
                <input
                  key={String(row.id ?? index)}
                  className="sala-input-dark mb-2 text-sm"
                  value={String(row.label ?? '')}
                  placeholder={`Categoría ${index + 1}`}
                  onChange={(e) => {
                    const next = categories.map((c, i) => (i === index ? { ...(c as object), label: e.target.value } : c));
                    setPayload({ ...payload, categories: next });
                  }}
                />
              );
            })}
          </Section>
          <Section title="Ítems">
            {items.map((item, index) => {
              const row = item as Record<string, unknown>;
              return (
                <div key={String(row.id ?? index)} className="mb-2 grid gap-2 sm:grid-cols-[1fr_140px]">
                  <input
                    className="sala-input-dark text-sm"
                    value={String(row.text ?? '')}
                    placeholder="Texto del ítem"
                    onChange={(e) => {
                      const next = items.map((it, i) => (i === index ? { ...(it as object), text: e.target.value } : it));
                      setPayload({ ...payload, items: next });
                    }}
                  />
                  <select
                    className="sala-input-dark text-sm"
                    value={String(row.categoryId ?? '')}
                    onChange={(e) => {
                      const next = items.map((it, i) => (i === index ? { ...(it as object), categoryId: e.target.value } : it));
                      setPayload({ ...payload, items: next });
                    }}
                  >
                    {categories.map((cat) => {
                      const c = cat as Record<string, unknown>;
                      return (
                        <option key={String(c.id)} value={String(c.id)}>
                          {String(c.label ?? c.id)}
                        </option>
                      );
                    })}
                  </select>
                </div>
              );
            })}
          </Section>
        </div>
      );
      break;
    }

    case 'structural_breakdown': {
      const lines = Array.isArray(payload.lines) ? payload.lines : [];
      editorBody = (
        <div className="space-y-4">
          <BuilderInput label="Título" value={String(payload.title ?? block.label ?? '')} onChange={(e) => setTitle(e.target.value)} />
          <label className="block">
            <span className="sala-label">Tipografía</span>
            <select
              className="sala-input-dark mt-2 w-full text-sm"
              value={String(payload.typography ?? 'serif')}
              onChange={(e) => setPayload({ ...payload, typography: e.target.value })}
            >
              <option value="serif">Serif (poesía / literatura)</option>
              <option value="sans">Sans (gramática / sintaxis)</option>
            </select>
          </label>
          <Section title="Líneas anotadas">
            {lines.map((line, index) => {
              const row = line as Record<string, unknown>;
              return (
                <div key={index} className="mb-3 grid gap-2 sm:grid-cols-[1fr_120px_auto]">
                  <input
                    className="sala-input-dark text-sm"
                    value={String(row.text ?? '')}
                    placeholder="Verso u oración"
                    onChange={(e) => {
                      const next = lines.map((item, i) =>
                        i === index ? { ...(item as object), text: e.target.value } : item,
                      );
                      setPayload({ ...payload, lines: next });
                    }}
                  />
                  <input
                    className="sala-input-dark text-sm"
                    value={String(row.badge ?? '')}
                    placeholder="A / Sujeto"
                    onChange={(e) => {
                      const next = lines.map((item, i) =>
                        i === index ? { ...(item as object), badge: e.target.value } : item,
                      );
                      setPayload({ ...payload, lines: next });
                    }}
                  />
                  <button
                    type="button"
                    className="text-xs text-red-500"
                    onClick={() => setPayload({ ...payload, lines: lines.filter((_, i) => i !== index) })}
                  >
                    ×
                  </button>
                </div>
              );
            })}
            <button
              type="button"
              className="sala-btn-outline text-xs"
              onClick={() => setPayload({ ...payload, lines: [...lines, { text: '', badge: '' }] })}
            >
              + Línea
            </button>
          </Section>
        </div>
      );
      break;
    }

    case 'source_analysis': {
      const source = (payload.source ?? {}) as Record<string, unknown>;
      const questions = Array.isArray(payload.questions) ? payload.questions.map(String) : [];
      editorBody = (
        <div className="space-y-4">
          <BuilderInput label="Título" value={String(payload.title ?? block.label ?? '')} onChange={(e) => setTitle(e.target.value)} />
          <BuilderTextarea
            label="Cita / extracto"
            value={String(source.quote ?? '')}
            onChange={(e) => setPayload({ ...payload, source: { ...source, quote: e.target.value } })}
          />
          <BuilderInput
            label="Referencia"
            hint="Autor, obra y fecha"
            value={String(source.reference ?? '')}
            onChange={(e) => setPayload({ ...payload, source: { ...source, reference: e.target.value } })}
          />
          <Section title="Preguntas abiertas">
            {questions.map((question, index) => (
              <div key={index} className="mb-2 flex gap-2">
                <textarea
                  className="sala-input-dark min-h-[4rem] flex-1 text-sm"
                  value={question}
                  placeholder={`Pregunta ${index + 1}`}
                  onChange={(e) => {
                    const next = questions.map((q, i) => (i === index ? e.target.value : q));
                    setPayload({ ...payload, questions: next });
                  }}
                />
                <button
                  type="button"
                  className="text-xs text-red-500"
                  onClick={() => setPayload({ ...payload, questions: questions.filter((_, i) => i !== index) })}
                >
                  ×
                </button>
              </div>
            ))}
            <button
              type="button"
              className="sala-btn-outline text-xs"
              disabled={questions.length >= 3}
              onClick={() => setPayload({ ...payload, questions: [...questions, ''] })}
            >
              + Pregunta
            </button>
          </Section>
        </div>
      );
      break;
    }

    case 'sandbox':
      editorBody = (
        <div className="space-y-4">
          <BuilderInput label="Título" value={String(payload.title ?? block.label ?? '')} onChange={(e) => setTitle(e.target.value)} />
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1 text-xs">
              <span className="font-semibold text-[var(--sala-field-dim)]">Variante</span>
              <select
                className="rounded border border-[var(--sala-border)] bg-[var(--sala-surface)] px-2 py-1.5 text-sm"
                value={String(payload.variant ?? 'balanza')}
                onChange={(e) => setPayload({ ...payload, variant: e.target.value })}
              >
                <option value="balanza">Balanza / pesos</option>
                <option value="fracciones">Fracciones</option>
                <option value="grafico">Economía / variación</option>
                <option value="inflation-market">Mercado inflacionario</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span className="font-semibold text-[var(--sala-field-dim)]">Máscara visual</span>
              <select
                className="rounded border border-[var(--sala-border)] bg-[var(--sala-surface)] px-2 py-1.5 text-sm"
                value={String(payload.skin ?? 'physics')}
                onChange={(e) => setPayload({ ...payload, skin: e.target.value })}
                disabled={payload.variant === 'inflation-market'}
              >
                <option value="physics">Física</option>
                <option value="algebra">Álgebra</option>
                <option value="economy">Economía</option>
                <option value="fractions">Fracciones</option>
              </select>
            </label>
          </div>
          {payload.variant !== 'inflation-market' && (
            <>
              <BuilderInput
                label="Valor objetivo"
                type="number"
                value={Number(payload.targetValue ?? 0)}
                onChange={(e) => setPayload({ ...payload, targetValue: Number(e.target.value) })}
              />
              <BuilderInput
                label="Valor inicial"
                type="number"
                value={Number(payload.initialValue ?? 0)}
                onChange={(e) => setPayload({ ...payload, initialValue: Number(e.target.value) })}
              />
              <BuilderInput
                label="Etiqueta de unidad"
                value={String(payload.unitLabel ?? 'kg')}
                onChange={(e) => setPayload({ ...payload, unitLabel: e.target.value })}
              />
            </>
          )}
          <Section title="Vista previa · impresión">
            <PrintMatrixPreview
              payload={payload}
              topicHint={String(payload.title ?? block.label ?? '')}
              subjectTag={examContext?.subjectTag}
            />
          </Section>
        </div>
      );
      break;

    case 'info_block':
      editorBody = (
        <div className="space-y-4">
          <BuilderInput label="Título" value={String(payload.title ?? block.label ?? '')} onChange={(e) => setTitle(e.target.value)} />
          <BuilderInput
            label="Sección"
            value={String(payload.sectionTitle ?? '')}
            onChange={(e) => setPayload({ ...payload, sectionTitle: e.target.value })}
          />
          <BuilderTextarea
            label="Texto editorial"
            value={String(payload.bodyText ?? '')}
            onChange={(e) => setPayload({ ...payload, bodyText: e.target.value })}
          />
          <BuilderInput
            label="Figuras de referencia (tokens separados por coma)"
            hint="Ej: Q, E, H — se muestran como referencia visual, no como pregunta."
            value={
              Array.isArray(payload.visualTokenReference)
                ? payload.visualTokenReference.join(', ')
                : ''
            }
            onChange={(e) =>
              setPayload({
                ...payload,
                visualTokenReference: e.target.value
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean),
              })
            }
          />
        </div>
      );
      break;

    case 'subtitulo':
    case 'destacado':
    case 'figura':
    case 'glosario_tabla':
    case 'formula_destacada':
      editorBody = (
        <GuideEditorialBlockEditor
          mode={block.mode}
          payload={payload}
          label={block.label}
          onPayloadChange={setPayload}
          onTitleChange={setTitle}
          subjectTag={examContext?.subjectTag}
        />
      );
      break;

    case 'canvas_breakdown': {
      const tokens = normalizeTokenArray(payload.patternTokens as string[]);
      const rhythmMethod = (payload.rhythmMethod as 'tradicional' | 'takadimi' | 'kodaly') || 'tradicional';
      editorBody = (
        <div className="space-y-4">
          <BuilderInput label="Título" value={String(payload.title ?? block.label ?? '')} onChange={(e) => setTitle(e.target.value)} />
          <BuilderTextarea
            label="Explicación paso a paso"
            value={String(payload.bodyText ?? '')}
            onChange={(e) => setPayload({ ...payload, bodyText: e.target.value })}
          />
          <RhythmPatternStrip
            patternTokens={tokens}
            rhythmMethod={rhythmMethod}
            graphicMode={payload.graphicMode as never}
            showSlots={false}
            printMode={false}
          />
          <div className="flex flex-wrap gap-2">
            {MUSICAL_TOKENS.map((tok) => (
              <button
                key={tok}
                type="button"
                className="music-token-chip"
                title={tokenToLabel(tok, rhythmMethod)}
                onClick={() => setPayload({ ...payload, patternTokens: [...tokens, tok] })}
              >
                <VexRhythmGlyph token={tok} size={28} color="#e8e4dc" />
              </button>
            ))}
            <button
              type="button"
              className="studio-rail-btn text-[10px]"
              onClick={() => setPayload({ ...payload, patternTokens: tokens.slice(0, -1) })}
            >
              Quitar última
            </button>
          </div>
          <BuilderInput
            label="Guía silábica (Takadimi / Kodály)"
            value={String(payload.syllableGuide ?? '')}
            onChange={(e) => setPayload({ ...payload, syllableGuide: e.target.value })}
          />
          <BuilderInput
            label="Compás de referencia (número)"
            type="number"
            value={Number(payload.targetBarNumber ?? 1)}
            onChange={(e) => setPayload({ ...payload, targetBarNumber: Number(e.target.value) })}
          />
        </div>
      );
      break;
    }

    default:
      if (isMusicNativeMode(block.mode)) {
        editorBody = (
          <div className="space-y-4">
            <BuilderInput label="Título" value={String(payload.title ?? block.label ?? '')} onChange={(e) => setTitle(e.target.value)} />
            <MusicCanvasEditor mode={block.mode} payload={payload} onPayloadChange={setPayload} />
          </div>
        );
      } else if (isGuideEditorialMode(block.mode)) {
        editorBody = (
          <GuideEditorialBlockEditor
            mode={block.mode}
            payload={payload}
            label={block.label}
            onPayloadChange={setPayload}
            onTitleChange={setTitle}
            subjectTag={examContext?.subjectTag}
          />
        );
      } else {
        editorBody = <AdvancedPayloadEditor payload={payload} onPayloadChange={setPayload} />;
      }
      break;
  }

  return (
    <>
      {canAi && examContext && (
        <>
          <div className="mb-3 flex items-center justify-between gap-2 border-b border-[var(--sala-border)] pb-3">
            <p className="text-[10px] text-[var(--sala-ink-dim)]">Edita manualmente o regenera solo este bloque.</p>
            <button
              type="button"
              className="sala-btn-hero py-1 text-[10px]"
              onClick={() => setAiOpen(true)}
            >
              🪄 Regenerar con IA
            </button>
          </div>
          <BlockAiGenerateDialog
            open={aiOpen}
            onOpenChange={setAiOpen}
            block={block}
            examContext={examContext}
            onGenerated={(next) => onChange(validateExamBlock(next))}
          />
        </>
      )}
      {editorBody}
    </>
  );
}

export function validateExamBlock(block: EvaluationBlock): EvaluationBlock {
  const validated = validateActivityPayload(block.mode, block.payload);
  const title = String((validated as { title?: string }).title ?? block.label ?? '').trim();
  return {
    ...block,
    label: title || block.label,
    payload: validated as EvaluationBlock['payload'],
  };
}
